import {
  BackgroundContract,
  BackgroundDefinitionContract,
  characterActions,
  characterSelectors,
} from "@dndbeyond/character-rules-engine/es";

import {
  createCharacterActionDescriptor,
  DescribedItem,
} from "../helpers/descriptor";
import type { CharacterActionMapper } from "../middleware";

type BackgroundHasCustomSetRequestAction = ReturnType<
  typeof characterActions.backgroundHasCustomSetRequest
>;

interface BackgroundHasCustomSetCommitAction {
  type: string;
  payload: { hasCustomBackground: boolean };
}

interface BackgroundSetCommitAction {
  type: string;
  payload: { background: BackgroundContract | null };
}

// A custom background isn't a background definition, so it has no ITEM_ID
const CUSTOM_BACKGROUND: DescribedItem = {
  name: "Custom Background",
};

// Choosing a background clears hasCustomBackground before the save
// (handleBackgroundSetRequest commits it first), so the state before the save
// commit no longer shows the background was custom. Remember the switch away
// from custom until the next background save commit.
let leavingCustomBackground = false;

export const resetBackgroundMappersForTests = () => {
  leavingCustomBackground = false;
};

const toItem = (definition: BackgroundDefinitionContract): DescribedItem => ({
  itemId: definition.id,
  name: definition.name ?? undefined,
});

/**
 * Choosing or removing a background.
 * BACKGROUND_CHOOSE only carries an id, and the background list lives in the
 * page, so this maps the commit that stores the saved background. It only
 * fires after a successful save, and only when the background changed (the
 * same commit follows other saves such as background choices).
 */
export const mapBackgroundSetCommit: CharacterActionMapper<
  BackgroundSetCommitAction
> = (action, prevState) => {
  const leftCustom = leavingCustomBackground;
  leavingCustomBackground = false;

  const next = action.payload.background;
  // Switching to a custom background is reported from its request
  if (next?.hasCustomBackground) return null;

  // A custom background's stored definition isn't what the user had selected
  const previousDefinition = leftCustom
    ? null
    : characterSelectors.getBackground(prevState)?.definition ?? null;
  const nextDefinition = next?.definition ?? null;
  if (!leftCustom && previousDefinition?.id === nextDefinition?.id) {
    return null;
  }

  const changeDescriptors = createCharacterActionDescriptor(
    leftCustom
      ? CUSTOM_BACKGROUND
      : previousDefinition
      ? toItem(previousDefinition)
      : null,
    nextDefinition ? toItem(nextDefinition) : null
  );
  if (!changeDescriptors.length) return null;

  return { changeCategory: "BACKGROUND", changeDescriptors };
};

/**
 * Switching to a custom background
 */
export const mapBackgroundHasCustomSet: CharacterActionMapper<
  BackgroundHasCustomSetRequestAction
> = (action, prevState) => {
  if (!action.payload.hasCustomBackground) return null;

  const previous = characterSelectors.getBackground(prevState);
  if (previous?.hasCustomBackground) return null;

  return {
    changeCategory: "BACKGROUND",
    changeDescriptors: createCharacterActionDescriptor(
      previous?.definition ? toItem(previous.definition) : null,
      CUSTOM_BACKGROUND
    ),
  };
};

/**
 * Notes when the background stops being custom, so the next save commit can
 * deselect Custom Background. Never sends an event itself.
 */
export const mapBackgroundHasCustomSetCommit: CharacterActionMapper<
  BackgroundHasCustomSetCommitAction
> = (action, prevState) => {
  const wasCustom =
    characterSelectors.getBackground(prevState)?.hasCustomBackground ?? false;
  leavingCustomBackground = wasCustom && !action.payload.hasCustomBackground;
  return null;
};
